/* Libraries */
import React, { useCallback, useMemo } from 'react';
import Tooltip from '@mui/material/Tooltip';

/* Components */
import Table from '~/src/containers/Table';
import RowActions from '~/src/components/RowActions';
import AddButtonOrAddedIcon from '~/src/components/AddButtonOrAddedIcon/AddButtonOrAddedIcon';
import { Actions } from '../RowActions/RowActions';
import { getFormattedDateFromIsoString } from '~/src/utils/date';

// Define styles outside component to prevent recreation on every render
const nameColumnStyle = {
  width: '38%',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
};

const dateColumnStyle = {
  width: '10%',
  textAlign: 'left',
};

const actionsColumnStyle = {
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
};

interface TableRowData {
  id: number;
  title: string;
  lastModified: string;
  template: any;
  isSourceDeprecated?: boolean;
  courtFormTitle?: string;
}

interface Props {
  data: TableRowData[];
  empty: boolean;
  visibleItems: number;
  rowActions?: Actions<any>;
  containedInSidebar: (item: any) => boolean;
  isAddButtonDisabled: boolean;
  getAddButtonTooltip: string | null;
  addFormTemplateToSelectionSidebar: (item: any) => void;
  onSearchChange: (s: string) => void;
  onSortByChange: (property: string, order: number) => void;
}

const TemplateDefaultValuesTable = React.memo(
  ({
    data,
    empty,
    visibleItems,
    rowActions,
    containedInSidebar,
    isAddButtonDisabled,
    getAddButtonTooltip,
    addFormTemplateToSelectionSidebar,
    onSearchChange,
    onSortByChange,
  }: Props) => {
    const renderRowAction = useCallback(
      (rowData: TableRowData) => {
        return (
          <div className="flex">
            <div className="mr-6 flex items-center">
              <AddButtonOrAddedIcon
                templateDefaultValues={rowData}
                containedInSidebar={containedInSidebar}
                handleAddTemplateToSelectionSidebar={
                  addFormTemplateToSelectionSidebar
                }
                isAddButtonDisabled={isAddButtonDisabled}
                getAddButtonTooltip={getAddButtonTooltip}
              />
            </div>

            {rowActions && (
              <RowActions<any> actions={rowActions} template={rowData} />
            )}
          </div>
        );
      },
      [
        containedInSidebar,
        addFormTemplateToSelectionSidebar,
        isAddButtonDisabled,
        getAddButtonTooltip,
        rowActions,
      ],
    );

    const renderTitle = useCallback(
      (title: string, { rowData }: { rowData: TableRowData }) => {
        const deprecationTooltip = rowData.isSourceDeprecated
          ? 'This form is out of date or no longer offered. Please check your court forms library, and create a new form template.'
          : '';

        return (
          <Tooltip
            title={deprecationTooltip}
            placement="top-end"
            disableHoverListener={!rowData.isSourceDeprecated}
          >
            <div className="flex items-center gap-2">
              <span>{title}</span>
              {rowData.isSourceDeprecated && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    border: '1px solid #FDE68A',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Form out of date
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
      [],
    );

    const columns = useMemo(
      () => [
        {
          label: 'Form template name',
          dataKey: 'title',
          render: renderTitle,
          style: nameColumnStyle,
        },
        {
          label: 'Last updated',
          dataKey: 'lastModified',
          render: getFormattedDateFromIsoString,
          style: dateColumnStyle,
        },
        {
          label: 'Actions',
          hideLabel: true,
          render: renderRowAction,
          style: actionsColumnStyle,
        },
      ],
      [renderRowAction, renderTitle],
    );

    return (
      <div data-analyticsname="Form Template Actions" className="flex">
        <Table
          emptyTitle={'No form templates match that filter'}
          empty={empty}
          searchPlaceholder="Search form templates"
          data={data}
          visibleItems={visibleItems}
          onSearchChange={onSearchChange}
          onSortByChange={onSortByChange}
          columns={columns}
        />
      </div>
    );
  },
);

TemplateDefaultValuesTable.displayName = 'TemplateDefaultValuesTable';

export default TemplateDefaultValuesTable;
